import RecordedChampion from '../../../components/RecordedChampion.jsx'
import SchoolTeamCoverage from '../../bowl/SchoolTeamCoverage.jsx'
import { useMemo, useState } from 'react'
import {
  gapBuckets, daysToEvent, formatDate, formatCountdownFor, sportMeta, pursuitMeta, ownerName,
  makeComparator,
} from '../../../lib/championships.js'
import { Th } from './SortControls.jsx'
import { SelectAllTh, SelectCell } from './RowSelect.jsx'
import SubmitLink from './SubmitLink.jsx'
import HeroBadge, { PartnershipCell } from './HeroBadge.jsx'
import { NextStepCompact } from './NextStep.jsx'
import { SuggestedRep } from './AssignRep.jsx'
import { nationalsFirst } from '../recognition.js'
import DateStamp from './DateStamp.jsx'
import TradeChip from '../../trades/TradeChip.jsx'
import styles from './pursuit.module.css'

/**
 * THE HERO — alarm buckets with live counts, nearest-event first. Doubles as
 * the data-cleanup worklist: each bucket is a gap to close. Rows are clickable
 * (open the detail drawer + action console) and color-coded: a sport accent bar
 * on the name, a pursuit-status chip on the status.
 */
export default function GapsBoard({
  items, now, onOpen, selection = null, noun = 'Championship', dateLabel = 'Event date',
  dated = true, linkLabel = 'Official listing', gapOpts = undefined,
  /* Nationals-above-chapters (the Greek space) and the per-row recognition
     action. Both default off, so no existing space changes. */
  groupNationals = false, rowAction = null,
  /* THE ASSIGNMENT PROPOSAL on an unowned row. Off by default and passed only
     by the Championships space, where the reassignment write exists — a
     suggestion nobody can act on is noise. Display-only either way. */
  suggest = false,
}) {
  const buckets = useMemo(() => gapBuckets(items, now, gapOpts), [items, now, gapOpts])
  const [open, setOpen] = useState(buckets[0]?.key || null)
  const active = buckets.find((b) => b.key === open) || buckets[0]
  // Page size, not a cap — the bucket expands to its full list on demand.
  const PAGE = 200
  const [limit, setLimit] = useState(PAGE)
  const [sort, setSort] = useState({ field: 'date', dir: 'asc' })
  // A space with no date spine can't sort by one; fall back to the name so the
  // table never sorts on a column it doesn't render.
  const sortField = !dated && (sort.field === 'date' || sort.field === 'openDate') ? 'name' : sort.field
  const cmp = useMemo(() => makeComparator(sortField, sort.dir, now), [sortField, sort.dir, now])
  const list = useMemo(() => {
    const sorted = [...(active?.items || [])].sort(cmp)
    // A national organization and one of its chapters are different-sized
    // opportunities, so the nationals stay at the top of whatever sort is
    // active rather than being scattered through hundreds of chapters.
    return groupNationals ? nationalsFirst(sorted) : sorted
  }, [active, cmp, groupNationals])
  const shown = limit >= list.length ? list : list.slice(0, limit)
  // The official-listing column only exists where records actually carry one
  // (RFPs and the other pursuit lines) — a championship table is unchanged.
  const hasLinks = useMemo(() => list.some((c) => c.sourceUrl), [list])
  // Same rule for every pursuit-only column: STATE (the RFP's jurisdiction),
  // PRODUCT (what it's for), and the employee-recognition set — INDUSTRY,
  // TOTAL EMPLOYEES and HQ LOCATION — render only where records carry them, so
  // the championship gaps table keeps exactly the columns it always had.
  const hasProduct = useMemo(() => list.some((c) => c.product), [list])
  const hasState = useMemo(() => list.some((c) => c.sourceType && c.state && c.state !== 'NATIONAL'), [list])
  const hasOpen = useMemo(() => list.some((c) => c.openDate), [list])
  const hasIndustry = useMemo(() => list.some((c) => c.industry), [list])
  const hasEmployees = useMemo(() => list.some((c) => c.employees != null), [list])
  const hasHq = useMemo(() => list.some((c) => c.hqLocation), [list])
  // "Sport / level" is the championship pairing. Where there is no sport the
  // column is just the level — named the way this space names it.
  const hasSport = useMemo(() => list.some((c) => c.sport), [list])
  const levelLabel = useMemo(() => list.find((c) => c.levelLabel)?.levelLabel || 'Level', [list])
  const hqLabel = useMemo(() => list.find((c) => c.hqLabel)?.hqLabel || 'HQ location', [list])
  // THE RECOGNITION / PURSUIT COLUMNS. Same rule as every other pursuit-only
  // column: they render only where the records actually carry the data, so a
  // championship table — and a corporate one — keeps exactly its own columns.
  //
  // The hero column is keyed off the SPACE (`rowAction`), not off whether any
  // department happens to have a nomination yet: a Community Hero space with
  // nothing nominated must still show the empty column, because "nobody has a
  // hero" IS the worklist.
  const heroSpace = rowAction?.kind === 'hero'
  const hasHero = useMemo(() => heroSpace || list.some((c) => c.hero), [heroSpace, list])
  const hasOrgType = useMemo(() => list.some((c) => c.orgType), [list])
  const hasPursuitDate = useMemo(() => list.some((c) => c.pursuitDate), [list])
  const hasWonDate = useMemo(() => list.some((c) => c.wonDate), [list])
  // THE CHEER GYM COLUMNS. Keyed off the SPACE (`rowAction`), not off whether
  // any gym happens to be partnered yet — in a gym space "nobody is partnered"
  // IS the worklist, exactly as "nobody has a hero" is on the police one.
  const gymSpace = rowAction?.kind === 'partnership'
  const hasPartnership = useMemo(() => gymSpace || list.some((c) => c.partnershipStage), [gymSpace, list])

  return (
    <div className={styles.gaps}>
      <div className={styles.gapCards}>
        {buckets.map((b) => (
          <button
            key={b.key}
            className={`${styles.gapCard} ${open === b.key ? styles.gapCardOn : ''}`}
            onClick={() => { setOpen(b.key); setLimit(PAGE) }}
          >
            <div className={styles.gapCount}>{b.items.length.toLocaleString()}</div>
            <div className={styles.gapLabel}>{b.label}</div>
            <div className={styles.gapHint}>{b.hint}</div>
          </button>
        ))}
      </div>

      {active && (
        <div className={styles.gapDetail}>
          <div className={styles.gapDetailHead}>
            <span className={styles.gapDetailTitle}>{active.label}</span>
            <span className={styles.gapDetailSub}>
              showing {shown.length.toLocaleString()} of {list.length.toLocaleString()} records · click a column to sort
              (incl. Owner) · click a row to open
            </span>
          </div>
          {/* Below 640px this table becomes a stack of cards — see
              src/styles/cc-mobile.css. `cc-cards` / `cc-cards-wrap` are the
              hooks; the per-cell `data-col` / `data-label` attributes below
              carry the order and the labels. Nothing is dropped and the DOM
              is unchanged, so sorting and the row click still work. */}
          <div className={`${styles.tableWrap} cc-scrollcue cc-cards-wrap`}>
            <table className={`${styles.table} cc-cards`}>
              <thead>
                <tr>
                  {/* Select-all governs the whole open bucket, not the page. */}
                  <SelectAllTh rows={list} selection={selection} label={`rows in ${active.label}`} />
                  <Th field="name" label={noun} sort={sort} setSort={setSort} />
                  {list.some(c => !c.sourceType) && <th>Champion / recorded claim</th>}
                  <Th
                    field={hasSport ? 'sport' : 'level'}
                    label={hasSport ? 'Sport / level' : levelLabel}
                    sort={sort}
                    setSort={setSort}
                  />
                  {/* WHAT KIND OF BODY IT IS — national vs chapter, and the
                      department type. */}
                  {hasOrgType && <Th field="orgType" label="Type" sort={sort} setSort={setSort} />}
                  {hasIndustry && <Th field="industry" label="Industry" sort={sort} setSort={setSort} />}
                  {/* HEADCOUNT — the size of the recognition program. */}
                  {hasEmployees && <Th field="employees" label="Total employees" sort={sort} setSort={setSort} />}
                  {hasHq && <Th field="hqLocation" label={hqLabel} sort={sort} setSort={setSort} />}
                  {/* THE COMMUNITY HERO. One per precinct — the headline column
                      of a police / fire space, and the gap when it's empty. */}
                  {hasHero && <Th field="heroStatus" label={rowAction?.heroLabel || 'Community Hero'} sort={sort} setSort={setSort} />}
                  {/* THE GYM PARTNERSHIP — the pipeline stage (sorted in
                      pipeline order, not alphabetically) and the championship
                      footprint the gym brings with it. Brian's ask: sort the
                      TAM by established championship relationships. */}
                  {hasPartnership && <Th field="partnership" label="Partnership" sort={sort} setSort={setSort} />}
                  {hasPartnership && <Th field="titles" label="Championships" sort={sort} setSort={setSort} />}
                  {hasPartnership && <Th field="teamCount" label="Teams" sort={sort} setSort={setSort} />}
                  {hasProduct && <Th field="product" label={list.find((c) => c.productLabel)?.productLabel === 'Product pursued' ? 'Product pursued' : 'Product'} sort={sort} setSort={setSort} />}
                  {/* THE GREEK PURSUIT DATES — when it opened, when it landed. */}
                  {hasPursuitDate && <Th field="pursuitDate" label="Pursuit date" sort={sort} setSort={setSort} />}
                  {hasWonDate && <Th field="wonDate" label="Won date" sort={sort} setSort={setSort} />}
                  {hasOpen && <Th field="openDate" label="Surfaced" sort={sort} setSort={setSort} />}
                  {dated && <Th field="date" label={dateLabel} sort={sort} setSort={setSort} />}
                  {dated && <th>Countdown</th>}
                  {hasState && <Th field="state" label="State" sort={sort} setSort={setSort} />}
                  <Th field="owner" label="Assigned rep" sort={sort} setSort={setSort} />
                  <Th field="pursuit" label="Status" sort={sort} setSort={setSort} />
                  {hasLinks && <th>{linkLabel}</th>}
                  {/* THE GUIDED NEXT STEP — the last column, and the only one
                      that tells the rep what to DO. Every other column is a
                      fact; this one is the move. Only in a space with a
                      pathway. */}
                  {rowAction && <th>Next step</th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => {
                  const days = daysToEvent(c, now)
                  const sm = sportMeta(c.sport)
                  const pm = pursuitMeta(c.pursuit)
                  const owner = ownerName(c)
                  return (
                    <tr key={c.id} className={onOpen ? styles.rowClickable : ''} onClick={onOpen ? () => onOpen(c) : undefined}>
                      <SelectCell item={c} selection={selection} />
                      <td className={styles.tName} data-col="name">
                        {/* The accent encodes the SPORT. In a space with none
                            (Corporate) it would be a colour that means nothing,
                            so it isn't drawn. */}
                        {hasSport && <span className={styles.sportAccent} style={{ background: sm.color }} title={sm.label} />}
                        {c.name}{onOpen && <span className={styles.openHint}> ›</span>}
                      </td>
                      {list.some(row => !row.sourceType) && <td data-col="champion" data-label="Champion / recorded claim"><RecordedChampion record={c} now={now} /></td>}
                      <td className={styles.tDim} data-col="sport" data-label={hasSport ? 'Sport / level' : levelLabel}>
                        {hasSport && <span className={styles.sportDot} style={{ background: sm.color }} />}
                        {[c.sport, c.level].filter(Boolean).join(' · ') || '—'}
                      </td>
                      {hasOrgType && <td className={c.orgType ? styles.tDim : styles.tGap} data-col="orgType" data-label="Type">{c.orgType || '—'}</td>}
                      {hasIndustry && <td className={c.industry ? styles.tDim : styles.tGap} data-col="industry" data-label="Industry">{c.industry || '—'}</td>}
                      {/* An unknown headcount is unknown — never a zero. */}
                      {hasEmployees && (
                        <td className={c.employees != null ? styles.tNum : styles.tGap} data-col="employees" data-label="Total employees">
                          {c.employees != null ? c.employees.toLocaleString() : 'unknown'}
                        </td>
                      )}
                      {hasHq && <td className={c.hqLocation ? styles.tDim : styles.tGap} data-col="hq" data-label={hqLabel}>{c.hqLocation || '—'}</td>}
                      {/* No hero yet is the honest, and useful, state — it is
                          the reason the department is on this list. */}
                      {hasHero && <td data-col="hero" data-label={rowAction?.heroLabel || 'Community Hero'}><HeroBadge record={c} /></td>}
                      {hasPartnership && <td data-col="partnership" data-label="Partnership"><PartnershipCell record={c} /></td>}
                      {/* A gym with no matched title reads 0 — a real answer
                          and the untouched end of the TAM, never "unknown". */}
                      {hasPartnership && (
                        <td className={c.titleCount ? styles.tNum : styles.tGap} data-col="titles" data-label="Championships">{c.titleCount ?? 0}</td>
                      )}
                      {hasPartnership && (
                        <td className={c.teamCount ? styles.tNum : styles.tGap} data-col="teamCount" data-label="Teams">{c.teamCount ?? 0}</td>
                      )}
                      {hasProduct && <td className={c.product ? styles.tDim : styles.tGap} data-col="product" data-label="Product">{c.product || 'none pursued'}</td>}
                      {hasPursuitDate && (
                        <td className={c.pursuitDate ? styles.tNum : styles.tGap} data-col="pursuitDate" data-label="Pursuit date">
                          {c.pursuitDate ? formatDate(c.pursuitDate) : '—'}
                        </td>
                      )}
                      {/* A won date only ever exists on a WON deal, so a blank
                          here means "not won", never "date missing". */}
                      {hasWonDate && (
                        <td className={c.wonDate ? styles.tNum : styles.tDim} data-col="wonDate" data-label="Won date">
                          {c.wonDate ? formatDate(c.wonDate) : <span className={styles.tDim}>not won</span>}
                        </td>
                      )}
                      {hasOpen && (
                        <td className={c.openDate ? styles.tDim : styles.tGap} data-col="openDate" data-label="Surfaced" title={c.openDateIsProxy ? 'Scan date — when the engine surfaced this posting' : undefined}>
                          {c.openDate ? formatDate(c.openDate) : '—'}
                        </td>
                      )}
                      {dated && <td className={c.effDate ? '' : styles.tGap} data-col="date" data-label={dateLabel}>{c.effDate ? formatDate(c.effDate) : 'NO DATE'}{c.dateEstimated && <span className={styles.estBadge} title="Estimated date">est</span>}{' '}<DateStamp c={c} compact /></td>}
                      {dated && <td className={styles.tNum} data-col="countdown" data-label="Countdown">{formatCountdownFor(c, days)}</td>}
                      {hasState && (
                        <td className={c.state && c.state !== 'NATIONAL' ? styles.tDim : styles.tGap} data-col="state" data-label="State">
                          {c.state && c.state !== 'NATIONAL' ? c.state : '—'}
                        </td>
                      )}
                      {/* Nobody on it is the gap; the engine's proposal is the
                          answer to it, sitting right where the owner would be. */}
                      <td className={owner ? styles.tDim : styles.tGap} data-col="owner" data-label="Assigned rep">
                        {owner || 'Unassigned'}
                        <SchoolTeamCoverage record={c}/>
                        {suggest && !owner && (
                          <div><SuggestedRep record={c} compact /></div>
                        )}
                        {/* The trade on this record, inline (2026-10-19). Nothing when there is none. */}
                        <TradeChip recordId={c.id} />
                      </td>
                      {/* A pursuit row's own status word ("Open") beats the
                          canonical bucket it maps to — that is the record. */}
                      <td data-col="status" data-label="Status"><span className={styles.statusChip} style={{ color: pm.color, borderColor: pm.color + '55', background: pm.color + '12' }}>{c.sourceType && c.stageLabel ? c.stageLabel : pm.label}</span></td>
                      {hasLinks && (
                        <td data-col="link" data-label={linkLabel}>
                          {c.sourceUrl
                            ? <SubmitLink record={c} />
                            : <span className={styles.tGap}>no link on record</span>}
                        </td>
                      )}
                      {rowAction && (
                        <td data-col="cta" data-label="Next step" onClick={(e) => e.stopPropagation()}>
                          <NextStepCompact record={c} action={rowAction} onOpen={onOpen} />
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {list.length > shown.length && (
              <div className={styles.more}>
                <button className={styles.moreBtn} onClick={() => setLimit(limit + PAGE * 5)}>
                  Show {Math.min(PAGE * 5, list.length - shown.length).toLocaleString()} more
                </button>
                <button className={styles.moreBtn} onClick={() => setLimit(list.length)}>
                  Show all {list.length.toLocaleString()}
                </button>
              </div>
            )}
            {list.length === 0 && <div className={styles.emptyState}>No records in this gap — clean.</div>}
          </div>
        </div>
      )}
    </div>
  )
}
